import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { MultiSelectFilter } from '@/components/policies/MultiSelectFilter';

export type PolicySortOption = 'next_review' | 'last_approved' | 'name_asc' | 'name_desc' | 'category';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'Approved', label: 'Approved' },
  { value: 'pending_approval', label: 'Pending Approval' },
  // separator after index 2
  { value: 'review_overdue', label: 'Over Due' },
  { value: 'review_due_soon', label: 'Due Soon' },
  { value: 'review_need_ack', label: 'Need Acknowledgment' },
];

interface PolicyFiltersProps {
  categories: string[];
  selectedCategories: string[];
  selectedStatuses: string[];
  selectedSort: PolicySortOption;
  onCategoriesChange: (categories: string[]) => void;
  onStatusesChange: (statuses: string[]) => void;
  onSortChange: (sort: PolicySortOption) => void;
  onClearFilters: () => void;
}

export function PolicyFilters({
  categories,
  selectedCategories,
  selectedStatuses,
  selectedSort,
  onCategoriesChange,
  onStatusesChange,
  onSortChange,
  onClearFilters,
}: PolicyFiltersProps) {
  const hasFilters = selectedCategories.length > 0 || selectedStatuses.length > 0;

  const categoryOptions = categories.map((c) => ({ value: c, label: c }));

  const statusLabelMap = useMemo(
    () => new Map(STATUS_OPTIONS.map((o) => [o.value, o.label])),
    []
  );

  return (
    <div className="flex flex-wrap items-center gap-2 min-h-[40px]">
      <MultiSelectFilter
        label="Categories"
        options={categoryOptions}
        selected={selectedCategories}
        onChange={onCategoriesChange}
      />

      <MultiSelectFilter
        label="Statuses"
        options={STATUS_OPTIONS}
        selected={selectedStatuses}
        onChange={onStatusesChange}
        separatorAfterIndex={2}
      />

      <Select
        value={selectedSort}
        onValueChange={(value) => onSortChange(value as PolicySortOption)}
      >
        <SelectTrigger className="w-full sm:w-[200px] bg-card border-border rounded-[8px]">
          <SelectValue placeholder="Sort by" />
        </SelectTrigger>
        <SelectContent className="z-50 bg-card rounded-[8px]">
          <SelectItem value="next_review">Sort: Next Review Date</SelectItem>
          <SelectItem value="last_approved">Sort: Last Approved Date</SelectItem>
          <SelectItem value="name_asc">Sort: Policy Name A→Z</SelectItem>
          <SelectItem value="name_desc">Sort: Policy Name Z→A</SelectItem>
          <SelectItem value="category">Sort: Category</SelectItem>
        </SelectContent>
      </Select>

      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          Clear filters
        </Button>
      )}

      {/* Inline scrollable chip area */}
      {hasFilters && (
        <div className="flex items-center gap-1.5 overflow-x-auto flex-nowrap scrollbar-hide min-w-0">
          {selectedCategories.map((value) => (
            <Badge
              key={`cat-${value}`}
              variant="secondary"
              className="text-xs py-0 pl-2 pr-1 gap-1 cursor-pointer whitespace-nowrap shrink-0"
              onClick={() => onCategoriesChange(selectedCategories.filter((v) => v !== value))}
            >
              {value}
              <X className="h-3 w-3" />
            </Badge>
          ))}
          {selectedStatuses.map((value) => (
            <Badge
              key={`status-${value}`}
              variant="secondary"
              className="text-xs py-0 pl-2 pr-1 gap-1 cursor-pointer whitespace-nowrap shrink-0"
              onClick={() => onStatusesChange(selectedStatuses.filter((v) => v !== value))}
            >
              {statusLabelMap.get(value) ?? value}
              <X className="h-3 w-3" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
